import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useCurrentUser } from '@/hooks/api/useAccount';
import {
  useBitbucketImportedUsers,
  useBitbucketStatus,
} from '@/hooks/api/useBitbucket';
import { useGithubImportedUsers, useGithubStatus } from '@/hooks/api/useGithub';
import { useMembers, useRefreshMemberRoster } from '@/hooks/api/useMembers';
import { useSecurityGroupByUser } from '@/hooks/api/useSecurityGroups';
import type { FilterValue } from '@/components/DataTable/types';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useIsSupport } from '@/hooks/useRoleCheck';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { ApiError } from '@kirin/api-client/errors';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { type ReactElement, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AddMemberModal } from '../components/AddMemberModal';
import { MembersTable } from '../components/MembersTable';
import { isAccountMember } from '@/lib/member-kinds';

// TODO: Defer Platforms column implementation for now
// This will be added in a future phase when backend supports platform tracking

/**
 * Main Members tab component
 */
export default function MembersTab(): ReactElement {
  const { organizationId } = useParams<{ organizationId: string }>();
  // Keyed on the org: the tab stays mounted across org switches, so without a
  // remount the search term and "Discovered only" toggle (KIR-4921) would carry
  // over and silently hide the next org's users.
  return <MembersTabContent key={organizationId} />;
}

function MembersTabContent(): ReactElement | null {
  const [showAddModal, setShowAddModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // KIR-3908: filter the list to Discovered/Unattributed users only.
  const [showOnlyDiscovered, setShowOnlyDiscovered] = useState(false);
  const { organizationId } = useParams<{ organizationId: string }>();
  // KIR-4921: client-side name/email/handle filter (see MembersTable).
  const [search, setSearch] = useState('');
  // KIR-5060: the table's header filters. Held here, not in MembersTable, so
  // they survive the table unmounting while imported users load.
  const [filters, setFilters] = useState<FilterValue[]>([]);

  // Check if user is support
  const { isSupport } = useIsSupport(organizationId);

  // API hooks for members
  const {
    data: membersData,
    isLoading: membersLoading,
    error: membersError,
  } = useMembers(organizationId);
  // KIR-4887: Refresh bypasses the backend's cached roster (fresh=true).
  const refreshMembers = useRefreshMemberRoster(organizationId);
  const members = useMemo(() => membersData ?? [], [membersData]);
  // React Query keeps the last successful `data` even when a later background
  // refetch (refetch-on-focus / manual Refresh) errors, so a loaded list stays
  // trustworthy across transient failures. `data === undefined`
  // is the only state with no trustworthy list: the initial load is still
  // pending, or it failed before ever returning data (KIR-4040).
  const hasLoadedMembers = membersData !== undefined;
  // KIR-4887: 503 = Descope is rate-limiting and the backend has no cached
  // roster. Temporary — say so instead of the generic failure.
  const isMembersTemporarilyUnavailable =
    membersError instanceof ApiError && membersError.status === 503;
  const { data: currentUser } = useCurrentUser();
  const currentUserId = currentUser?.id || '';

  // Check if integration feature flags are enabled
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.GITHUB_INTEGRATION,
    flagsmithFeatureFlags.BITBUCKET_INTEGRATION,
    flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES,
  ]);
  // KIR-3800 (AC #11). The members API carries no group field, so the column is
  // fed by a derived map; gated so no group request fires when the flag is off.
  const isSecurityGroupsEnabled =
    flags[flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES] ?? false;
  const { groupByUserId, groups: securityGroups } = useSecurityGroupByUser(
    organizationId,
    isSecurityGroupsEnabled
  );
  // Every group, so the Security Group filter can find one with no members yet.
  const securityGroupNames = useMemo(
    () => securityGroups.map(group => group.name),
    [securityGroups]
  );
  const securityGroupNameByUserId = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(groupByUserId).map(([userId, group]) => [
          userId,
          group.name,
        ])
      ),
    [groupByUserId]
  );
  const isGithubIntegrationEnabled =
    import.meta.env.VITE_FF_GITHUB_INTEGRATION === 'true' ||
    !!flags[flagsmithFeatureFlags.GITHUB_INTEGRATION];
  const isBitbucketIntegrationEnabled =
    import.meta.env.VITE_FF_BITBUCKET_INTEGRATION === 'true' ||
    !!flags[flagsmithFeatureFlags.BITBUCKET_INTEGRATION];

  // Check if GitHub is connected to fetch imported users
  const { data: githubStatus } = useGithubStatus(organizationId ?? '');
  const isGithubConnected = githubStatus?.connected === true;

  // Check if Bitbucket is connected to fetch imported users
  const { data: bitbucketStatus } = useBitbucketStatus(organizationId ?? '');
  const isBitbucketConnected = bitbucketStatus?.connected === true;

  // Fetch GitHub imported users
  const {
    data: githubImportedResult,
    isLoading: githubImportedLoading,
    refetch: refetchGithubImported,
  } = useGithubImportedUsers(organizationId ?? '', {
    enabled: isGithubConnected,
  });

  // Fetch Bitbucket imported users
  const {
    data: bitbucketImportedResult,
    isLoading: bitbucketImportedLoading,
    refetch: refetchBitbucketImported,
  } = useBitbucketImportedUsers(organizationId ?? '', {
    enabled: isBitbucketConnected,
  });

  // Merge imported users from all providers
  const importedUsers = useMemo(() => {
    const github =
      isGithubConnected &&
      githubImportedResult &&
      'users' in githubImportedResult
        ? githubImportedResult.users
        : [];
    const bitbucket =
      isBitbucketConnected &&
      bitbucketImportedResult &&
      'users' in bitbucketImportedResult
        ? bitbucketImportedResult.users
        : [];
    return [...github, ...bitbucket];
  }, [
    isGithubConnected,
    githubImportedResult,
    isBitbucketConnected,
    bitbucketImportedResult,
  ]);

  // Lower-cased emails already in the org (real members + pending invites) so
  // the Add Member modal can block re-adding an existing user (KIR-4040).
  // KIR-3908: exclude Discovered/Unattributed rows — they are NOT accounts, and
  // Add Member is a valid path to convert a Discovered user (accept-time merge
  // folds its history), so blocklisting their email would wrongly reject it.
  const existingEmails = useMemo(
    () =>
      members
        // KIR-5410: an offboarded person can be re-invited, so not blocked.
        .filter(isAccountMember)
        .map(m => m.email)
        .filter((e): e is string => Boolean(e))
        .map(e => e.toLowerCase()),
    [members]
  );

  if (!organizationId) {
    return null;
  }

  const isLoading =
    membersLoading ||
    (isGithubConnected && githubImportedLoading) ||
    (isBitbucketConnected && bitbucketImportedLoading);

  const hasAnyIntegration =
    isGithubIntegrationEnabled || isBitbucketIntegrationEnabled;

  // KIR-3908: Discovered/Unattributed users always need the Status + Source
  // columns (to show their kind + "Cowork"), even without an SCM integration.
  const hasDiscoveredUsers = members.some(
    m => m.userType && m.userType !== 'full'
  );
  // KIR-5410: offboarded rows carry their status in the Status column.
  const hasOffboardedUsers = members.some(m => !!m.offboardedAt);
  const showStatusAndSourceColumns =
    hasAnyIntegration || hasDiscoveredUsers || hasOffboardedUsers;
  // The toggle only renders while Discovered users exist, so it only applies
  // then too: once the last one is invited and becomes a full member, a still-set
  // `showOnlyDiscovered` would empty the table with the button gone. Reset the
  // state as well (React's adjust-state-during-render pattern), or a Discovered
  // user appearing on a later refetch would silently re-apply a filter the
  // admin can no longer see.
  if (showOnlyDiscovered && !hasDiscoveredUsers) {
    setShowOnlyDiscovered(false);
  }
  const isDiscoveredFilterActive = showOnlyDiscovered && hasDiscoveredUsers;
  // Same for a header filter whose column just disappeared: drop it, or it
  // keeps filtering rows with no control left to clear it.
  const shownFilters = filters.filter(
    f =>
      (f.field !== 'status' || showStatusAndSourceColumns) &&
      (f.field !== 'securityGroup' || isSecurityGroupsEnabled)
  );
  if (shownFilters.length !== filters.length) {
    setFilters(shownFilters);
  }
  const visibleMembers = isDiscoveredFilterActive
    ? members.filter(m => m.userType && m.userType !== 'full')
    : members;

  const handleRefresh = async (): Promise<void> => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refreshMembers(),
        isGithubConnected ? refetchGithubImported() : Promise.resolve(),
        isBitbucketConnected ? refetchBitbucketImported() : Promise.resolve(),
      ]);
    } finally {
      // Add a small delay so the spinner is visible
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  return (
    <Card>
      <CardContent>
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Search by name, email or handle"
              aria-label="Search users"
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            {hasDiscoveredUsers && (
              <Button
                variant={isDiscoveredFilterActive ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowOnlyDiscovered(v => !v)}
              >
                Discovered only
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing || isLoading}
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`}
              />
              Refresh
            </Button>
            {/*
            Also gate on the members query state (KIR-4040): the Add Member
            modal's duplicate guard is fed `existingEmails` derived from the
            loaded members list. Disable until a list has actually loaded
            (`hasLoadedMembers`) so the modal never opens with an incomplete
            blocklist. We intentionally do NOT disable on `membersError` alone —
            React Query keeps the cached list when a background refetch errors,
            so `existingEmails` stays trustworthy and the button should remain
            usable; only a never-loaded list (initial load pending or failed)
            blocks it.
          */}
            <Button
              onClick={() => setShowAddModal(true)}
              disabled={isSupport || !hasLoadedMembers}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Member
            </Button>
          </div>
        </div>
        {membersError &&
          (isMembersTemporarilyUnavailable ? (
            <div
              className="text-amber-600 dark:text-amber-400 text-sm mb-4"
              role="status"
            >
              The member list is temporarily unavailable due to high load.
              {hasLoadedMembers ? ' Showing the last loaded list. ' : ' '}
              Please try again in a minute.
            </div>
          ) : (
            <div className="text-red-600 text-sm mb-4">
              Failed to load members. Please try again.
            </div>
          ))}
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading members...
          </div>
        ) : (
          <MembersTable
            members={visibleMembers}
            importedUsers={isDiscoveredFilterActive ? [] : importedUsers}
            currentUserId={currentUserId}
            organizationId={organizationId}
            showStatusAndSourceColumns={showStatusAndSourceColumns}
            showSecurityGroupColumn={isSecurityGroupsEnabled}
            securityGroupNameByUserId={securityGroupNameByUserId}
            securityGroupNames={securityGroupNames}
            searchTerm={search}
            filters={shownFilters}
            onFiltersChange={setFilters}
          />
        )}
      </CardContent>

      {/* Add Member Modal */}
      <AddMemberModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        organizationId={organizationId}
        existingEmails={existingEmails}
      />
    </Card>
  );
}
